import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import GiftRecorder from 'Pages/public/gift-flow/GiftRecorder';
import { receiverSections } from 'Pages/public/gift-content/receiverQuestions';
import { generateSectionWithAI } from 'Utils/aiQuestionGenerator';
import NetflixPlayer from 'Components/NetflixPlayer';
import LightRays from 'Components/reactbits/LightRays';
import { isNewUIEnabled } from 'hooks/useNewUI';
import { PersonalVideoMessageFlowClassic } from 'Components/PersonalVideoMessageFlowClassic';

// Reusable "Add a personal video message" flow with a tile-hub UX.
//
// Three screens:
//   1. HUB     — info copy + a 2×2 grid of tiles (3 sections + "Other").
//                Each tile shows its completion state (Not started / X of N
//                partial / ✓ Done). A Finish button at the bottom switches
//                to the review screen.
//   2. SECTION RECORDER — opens when the user clicks a tile. Mounts
//                GiftRecorder scoped to that one section's prompts. The
//                per-section review UI is suppressed via skipReview, so as
//                soon as the user finishes the section the recorded clips
//                are stored and the user lands back on the hub. The "Other"
//                tile keeps GiftRecorder's regular review (no skipReview)
//                because the user needs an explicit "done adding" action.
//   3. REVIEW  — opens when the user clicks Finish on the hub. Shows every
//                stored clip grouped by section with inline previews and a
//                per-section "Re-record" link. Submit flattens and emits
//                onComplete(files, { fileSections }).
//
// State (in-memory only — closing the host or refreshing loses it):
//   clipsBySection: { [sectionTitle]: File[] }  // 'Other' key for custom
//
// Caller contract:
//   open / onClose          — host controls visibility.
//   onComplete(files, meta) — fires from Submit on the review screen.
//                             files is a flat array; meta.fileSections is a
//                             parallel array of section titles (or null for
//                             "Other"/custom) so the host can tag uploads.

const OTHER_KEY = 'Other';

const DEFAULT_INFO_TITLE = 'Tell your story';
const DEFAULT_INFO_SUBTITLE = 'Preserve your family history and legacy';
const DEFAULT_INFO_BODY = 'Your stories will land in your Projector along with your other Memories';

// ── Login-theme tokens ──────────────────────────────────────────────────────
// This flow is styled with inline styles (see the viewport note inside the
// component), so the login page's design system can't be composed in as CSS
// classes. Its values are mirrored here instead, copied from
// authentication/components/auth-theme.module.css and the token block at the
// top of gift-content/index.module.css, so the hub/review screens sit on
// exactly the same surface as their host page:
//   #060608 base · #5aa5ef accent · #f4f7fb text · #98a1b5 muted · Montserrat
// The recorder screen is deliberately NOT restyled — it's GiftRecorder, the
// same component (and look) the gift-flow sender uses.
const LP = {
  accent: '#5aa5ef',
  text: '#f4f7fb',
  muted: '#98a1b5',
  dim: '#7c869b',
  border: 'rgba(255, 255, 255, 0.09)',
  borderStrong: 'rgba(255, 255, 255, 0.18)',
  surface: 'rgba(255, 255, 255, 0.045)',
  surfaceHover: 'rgba(255, 255, 255, 0.085)',
  danger: '#ff6b6b',
  font: "'Montserrat', sans-serif",
  // Page backdrop — the theme's .rays wash over the near-black base.
  pageBg: 'radial-gradient(80% 45% at 50% -8%, rgba(255, 255, 255, 0.05), transparent 70%), #060608',
  // Glass card — theme .card.
  cardBg: 'linear-gradient(180deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.015) 100%), rgba(10, 10, 13, 0.66)',
  // Same card, opaque. Modals use this: a translucent surface lets whatever
  // sits behind read through the dialog, which looks broken rather than glassy.
  modalBg: 'linear-gradient(180deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.015) 100%), #0b0c11',
  cardShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.06), 0 28px 70px -24px rgba(0, 0, 0, 0.9)',
  blur: 'blur(22px) saturate(115%)',
  // Tiles sit straight on the backdrop, so they need a stronger wash than
  // .card's — same finding as the /choose-occasion and gift-content tiles.
  tileBg: 'linear-gradient(180deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.045) 100%), rgba(20, 22, 28, 0.72)',
  tileBlur: 'blur(18px) saturate(115%)',
  tileShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.06), 0 18px 40px -26px rgba(0, 0, 0, 0.9)',
  tileHoverShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 26px 52px -26px rgba(0, 0, 0, 0.95), 0 18px 40px -22px rgba(90, 165, 239, 0.55)',
  tileHoverBorder: 'rgba(90, 165, 239, 0.5)',
  primaryBg: 'linear-gradient(135deg, #5aa5ef 0%, #4a8ade 55%, #4172c8 100%)',
  primaryShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.18), 0 12px 26px -14px rgba(90, 165, 239, 0.9)',
  scrim: 'rgba(6, 6, 8, 0.88)',
};

// The hairline the theme draws along a card's top edge with ::before. Inline
// styles can't carry pseudo-elements, so here it's a real (decorative) node.
const CardRim = ({ inset = 24 }) => (
  <span
    aria-hidden="true"
    style={{
      position: 'absolute',
      top: 0,
      left: inset,
      right: inset,
      height: '1px',
      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.45), transparent)',
      pointerEvents: 'none',
    }}
  />
);

// The backdrop every themed screen in this flow sits on: volumetric light rays
// fanning down over the near-black base. Same layer and same props as
// /newSign-in and gift-flow (whose isThemedStep covers its recorder page too),
// so the hub, the recorder and the review all sit inside the beam.
const RaysLayer = () => (
  <div
    aria-hidden="true"
    style={{
      position: 'fixed',
      inset: 0,
      zIndex: 0,
      overflow: 'hidden',
      pointerEvents: 'none',
      background: LP.pageBg,
    }}
  >
    <LightRays
      raysOrigin="top-center"
      raysColor="#ffffff"
      raysSpeed={0.9}
      lightSpread={0.85}
      rayLength={1.25}
      fadeDistance={1.1}
      saturation={1}
      followMouse
      mouseInfluence={0.08}
      noiseAmount={0.08}
      distortion={0.04}
    />
  </div>
);

// Line icons in the app's usual lucide-style stroke, replacing the ✎ / 🗑 / ＋
// glyphs the tiles used to draw.
const PencilIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const TrashIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 6h18" />
    <path d="M8 6V4h8v2" />
    <path d="M18.5 6l-1 14h-11l-1-14" />
    <path d="M10 11v6M14 11v6" />
  </svg>
);

const PlusIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const PersonalVideoMessageFlowNew = ({
  open,
  onClose,
  onBack,
  onSkip,
  onComplete,
  infoTitle = DEFAULT_INFO_TITLE,
  infoSubtitle = DEFAULT_INFO_SUBTITLE,
  infoBody = DEFAULT_INFO_BODY,
  // When true, "Finish" submits the recorded clips immediately (kicks off the
  // upload) instead of routing through the review screen. Used by the
  // normal-user signup flow.
  skipReview = false,
  // When true, mirror the browser Back button to this overlay's internal
  // sections: each forward move (hub → recording, opening a modal) gets a
  // browser-history entry, and Back unwinds one section (recording/review → hub,
  // hub → close to content view). Only the new-user signup flow opts in.
  pinBrowserBack = false,
  // Free / no-plan users: block recording a tile or creating a new one — call
  // onBlockedRecording (show the upgrade popup) instead of entering the recorder.
  // The hub, Next/Skip/Back/Close still work so the user can proceed.
  blockRecording = false,
  onBlockedRecording,
}) => {
  // 'hub' | 'recording' | 'review'
  const [step, setStep] = useState('hub');
  // Skip confirmation popup (hub) — guards the user against leaving the
  // "Tell your story" flow for good. Skipping keeps the one-time "asked" flag
  // set, so this page won't be offered again; Back clears it (see onBack).
  const [skipConfirmOpen, setSkipConfirmOpen] = useState(false);
  // The stable id of the section being recorded. Never changes when its
  // display title is edited, so clips stay attached across renames.
  const [activeSectionTitle, setActiveSectionTitle] = useState(null);
  // Per-section recorder snapshot keyed by stable section id. Shape:
  //   { [sectionId]: { liveQuestions: [...], slots: [{ status, file, ... }] } }
  // We persist the FULL recorder state — not just the files — so that
  // re-entering a section restores:
  //   • the slot order / additions / deletions the user made
  //   • each slot's recorded clip (when one exists) with status DONE
  //   • the empty slots they haven't recorded yet
  // The recorder emits this snapshot in onComplete's meta.snapshot and as
  // onCancel's only argument, so both Finish and Back paths preserve it.
  const [sectionSnapshots, setSectionSnapshots] = useState({});
  // Files-by-section view of the snapshots — kept as a derived computation
  // so all the count/totals/review code below works unchanged. A section's
  // clips are the slot.file entries whose status is DONE, in slot order.
  const clipsBySection = useMemo(() => {
    const out = {};
    Object.keys(sectionSnapshots).forEach((id) => {
      const snap = sectionSnapshots[id];
      const files = ((snap && snap.slots) || [])
        .filter((s) => s && s.file)
        .map((s) => s.file);
      if (files.length > 0) out[id] = files;
    });
    return out;
  }, [sectionSnapshots]);

  // Dynamic section list — the 3 preset sections + a custom "Other", all
  // editable / deletable, plus any sections the user adds. Each entry:
  //   { id, title, intro, questions, custom }
  // `questions` is the preset prompt list (empty for custom/added sections,
  // which behave like Other — the user authors their own prompts). `title`
  // is the editable display name and becomes the upload category.
  const buildInitialSections = useCallback(() => {
    const list = receiverSections.map((s) => ({
      id: s.title,
      title: s.title,
      intro: s.intro,
      questions: s.questions,
      custom: false,
    }));
    list.push({
      id: OTHER_KEY,
      title: 'Other',
      intro: 'Your own questions',
      questions: [],
      custom: true,
    });
    return list;
  }, []);
  const [sectionList, setSectionList] = useState(buildInitialSections);

  // Inline-edit state: which tile's title is being edited + the draft value.
  const [editingSectionKey, setEditingSectionKey] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  // Viewport tracking — this whole flow is styled with inline styles, which
  // always beat injected CSS classes, so media-query stylesheets can't make
  // it responsive. Instead we track the breakpoint here and branch the inline
  // style values below. `isMobile` ≤640px (phones), `isNarrow` ≤768px (small
  // tablets / large phones) — used to switch the tile grid to one column,
  // shrink the section title so the full question wraps, and keep the
  // rename/delete icons from overlapping the badge.
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 640 : false,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => setIsMobile(window.innerWidth <= 640);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const getSectionById = useCallback(
    (id) => sectionList.find((s) => s.id === id) || null,
    [sectionList],
  );
  const startEditSection = useCallback((id) => {
    setEditingSectionKey(id);
    const s = sectionList.find((x) => x.id === id);
    setEditingValue(s ? s.title : '');
  }, [sectionList]);
  const cancelEditSection = useCallback(() => {
    setEditingSectionKey(null);
    setEditingValue('');
  }, []);
  const saveEditSection = useCallback(() => {
    setEditingSectionKey((id) => {
      if (id) {
        const v = editingValue.trim();
        setSectionList((prev) => prev.map((s) => (
          s.id === id ? { ...s, title: v || s.title } : s
        )));
      }
      return null;
    });
    setEditingValue('');
  }, [editingValue]);

  // Add a blank custom section (behaves like Other) and jump into renaming
  // it. Used by the modal's "Create manually" path.
  const addBlankSection = useCallback(() => {
    const id = `cmt-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setSectionList((prev) => [...prev, {
      id,
      title: 'New section',
      intro: 'Start Recording',
      questions: [],
      custom: true,
    }]);
    setEditingSectionKey(id);
    setEditingValue('New section');
  }, []);

  // AI section creation modal state. Three steps: 'choose' (AI or manual),
  // 'describe' (free-form topic), 'review' (edit title + questions before
  // committing). The modal closes without writing anything if cancelled.
  const [aiModalOpen, setAIModalOpen] = useState(false);
  const [aiStep, setAIStep] = useState('choose');
  const [aiDescription, setAIDescription] = useState('');
  const [aiLoading, setAILoading] = useState(false);
  const [aiError, setAIError] = useState('');
  const [aiResult, setAIResult] = useState(null); // { title, questions: string[] }
  const [aiEditingIdx, setAIEditingIdx] = useState(-1);
  const [aiEditingValue, setAIEditingValue] = useState('');

  const closeAIModal = useCallback(() => {
    setAIModalOpen(false);
    setAIStep('choose');
    setAIDescription('');
    setAILoading(false);
    setAIError('');
    setAIResult(null);
    setAIEditingIdx(-1);
    setAIEditingValue('');
  }, []);

  // Entry point for the "+ Add section" tile — opens the AI-or-manual choice
  // modal. Direct insertion still lives in addBlankSection (manual path).
  const handleAddSection = useCallback(() => {
    if (blockRecording) { if (onBlockedRecording) onBlockedRecording(); return; }
    setAIModalOpen(true);
    setAIStep('choose');
    setAIDescription('');
    setAIError('');
    setAIResult(null);
  }, [blockRecording, onBlockedRecording]);

  const handleAIChooseManual = useCallback(() => {
    closeAIModal();
    addBlankSection();
  }, [closeAIModal, addBlankSection]);

  const handleAIChooseAI = useCallback(() => {
    setAIStep('describe');
    setAIError('');
  }, []);

  const runAIGenerate = useCallback(async (description) => {
    setAILoading(true);
    setAIError('');
    try {
      const result = await generateSectionWithAI(description);
      setAIResult(result);
      setAIStep('review');
    } catch (e) {
      setAIError((e && e.message) || 'Generation failed.');
    } finally {
      setAILoading(false);
    }
  }, []);

  const handleAIGenerate = useCallback(() => {
    runAIGenerate(aiDescription);
  }, [runAIGenerate, aiDescription]);

  // Regenerate from the review step — silently re-uses the same description.
  const handleAIRegenerate = useCallback(() => {
    setAIEditingIdx(-1);
    setAIEditingValue('');
    runAIGenerate(aiDescription);
  }, [runAIGenerate, aiDescription]);

  const handleAIBackToDescribe = useCallback(() => {
    setAIStep('describe');
    setAIError('');
    setAIEditingIdx(-1);
    setAIEditingValue('');
  }, []);

  const handleAITitleChange = useCallback((value) => {
    setAIResult((prev) => (prev ? { ...prev, title: value } : prev));
  }, []);

  const handleAIDeleteQuestion = useCallback((idx) => {
    setAIResult((prev) => {
      if (!prev) return prev;
      return { ...prev, questions: prev.questions.filter((_, i) => i !== idx) };
    });
    setAIEditingIdx((cur) => {
      if (cur === idx) { setAIEditingValue(''); return -1; }
      if (cur > idx) return cur - 1;
      return cur;
    });
  }, []);

  const handleAIStartEditQuestion = useCallback((idx) => {
    setAIEditingIdx(idx);
    setAIResult((prev) => {
      if (prev && prev.questions[idx] != null) setAIEditingValue(prev.questions[idx]);
      return prev;
    });
  }, []);

  const handleAISaveEditQuestion = useCallback(() => {
    setAIResult((prev) => {
      if (!prev) return prev;
      const v = aiEditingValue.trim();
      if (!v) {
        // Empty save → drop the question entirely.
        return { ...prev, questions: prev.questions.filter((_, i) => i !== aiEditingIdx) };
      }
      return {
        ...prev,
        questions: prev.questions.map((q, i) => (i === aiEditingIdx ? v : q)),
      };
    });
    setAIEditingIdx(-1);
    setAIEditingValue('');
  }, [aiEditingValue, aiEditingIdx]);

  const handleAICancelEditQuestion = useCallback(() => {
    setAIResult((prev) => {
      if (!prev) return prev;
      // If the question being cancelled was an unfinished new one (empty
      // original), drop it.
      if (aiEditingIdx >= 0 && !prev.questions[aiEditingIdx]) {
        return { ...prev, questions: prev.questions.filter((_, i) => i !== aiEditingIdx) };
      }
      return prev;
    });
    setAIEditingIdx(-1);
    setAIEditingValue('');
  }, [aiEditingIdx]);

  const handleAIAddQuestion = useCallback(() => {
    setAIResult((prev) => {
      if (!prev) return prev;
      const next = { ...prev, questions: [...prev.questions, ''] };
      setAIEditingIdx(next.questions.length - 1);
      setAIEditingValue('');
      return next;
    });
  }, []);

  // Commit the AI result as a real section in sectionList and close the
  // modal. The new section is pre-populated with its generated questions, so
  // recording mounts it via the preset (sections+skipReview) branch — same
  // as the built-in three sections.
  const handleAICommitSection = useCallback(() => {
    if (!aiResult) return;
    const title = (aiResult.title || '').trim() || 'New section';
    const questions = (aiResult.questions || [])
      .map((q) => (typeof q === 'string' ? q.trim() : ''))
      .filter(Boolean);
    if (questions.length === 0) {
      setAIError('Add at least one question before saving.');
      return;
    }
    const id = `cmt-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setSectionList((prev) => [...prev, {
      id,
      title,
      intro: 'Start Recording',
      questions,
      custom: true,
    }]);
    closeAIModal();
  }, [aiResult, closeAIModal]);

  // Delete a section (and its clips). Confirms first if it has recordings.
  const handleDeleteSection = useCallback((id) => {
    const clipCount = (clipsBySection[id] || []).length;
    const name = (sectionList.find((s) => s.id === id) || {}).title || 'this section';
    // eslint-disable-next-line no-alert
    if (clipCount > 0 && !window.confirm(`Delete "${name}" and its ${clipCount} recording${clipCount === 1 ? '' : 's'}?`)) {
      return;
    }
    setSectionList((prev) => prev.filter((s) => s.id !== id));
    setSectionSnapshots((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setEditingSectionKey((k) => (k === id ? null : k));
  }, [clipsBySection, sectionList]);

  // Cache object URLs per File so video previews are stable across renders
  // and we can revoke them on unmount to avoid memory leaks.
  const urlCacheRef = useRef(new Map());

  const getPreviewUrl = useCallback((file) => {
    if (!file) return '';
    const cache = urlCacheRef.current;
    if (!cache.has(file)) cache.set(file, URL.createObjectURL(file));
    return cache.get(file);
  }, []);

  useEffect(() => () => {
    // Revoke every cached object URL when the component unmounts.
    urlCacheRef.current.forEach((url) => {
      try { URL.revokeObjectURL(url); } catch (e) { /* ignore */ }
    });
    urlCacheRef.current.clear();
  }, []);

  // Tile data with derived completion state. `key` is the stable section id;
  // `title` is the editable display title. When the user has edited a
  // section's question list (added or removed prompts), the snapshot's
  // liveQuestions length is the authoritative total — that way "1 of 2"
  // reflects what they'll actually see on re-entry, not the original
  // preset count.
  const tiles = useMemo(() => sectionList.map((s) => {
    const clipCount = (clipsBySection[s.id] || []).length;
    const snap = sectionSnapshots[s.id];
    const snapTotal = snap && Array.isArray(snap.liveQuestions)
      ? snap.liveQuestions.length
      : null;
    const presetTotal = (s.questions && s.questions.length) || 0;
    const total = snapTotal != null ? snapTotal : presetTotal;
    let state = 'notStarted';
    if (total > 0 && clipCount >= total) state = 'done';
    else if (clipCount > 0) state = 'partial';
    return {
      key: s.id,
      title: s.title,
      subtitle: s.intro,
      total: total || null,
      clipCount,
      state,
      custom: s.custom,
    };
  }), [sectionList, clipsBySection, sectionSnapshots]);

  const totalClips = useMemo(
    () => Object.values(clipsBySection).reduce((acc, arr) => acc + (arr ? arr.length : 0), 0),
    [clipsBySection],
  );

  const handleTileClick = useCallback((sectionKey) => {
    if (blockRecording) { if (onBlockedRecording) onBlockedRecording(); return; }
    setActiveSectionTitle(sectionKey);
    setStep('recording');
  }, [blockRecording, onBlockedRecording]);

  const handleHubClose = useCallback(() => {
    setStep('hub');
    setActiveSectionTitle(null);
    setSectionSnapshots({});
    setSectionList(buildInitialSections());
    if (onClose) onClose();
  }, [onClose, buildInitialSections]);

  // Back (top-left) — same reset as close, but routes through onBack so the
  // host can clear the one-time "asked" flag and re-offer this page on the
  // next Next click. Falls back to onClose when no onBack is provided.
  const handleHubBack = useCallback(() => {
    setStep('hub');
    setActiveSectionTitle(null);
    setSectionSnapshots({});
    setSectionList(buildInitialSections());
    if (onBack) onBack();
    else if (onClose) onClose();
  }, [onBack, onClose, buildInitialSections]);

  // Confirmed Skip — same reset as close, but routes through onSkip so the
  // host can set the one-time "asked" flag and retire this page.
  const handleHubSkip = useCallback(() => {
    setStep('hub');
    setActiveSectionTitle(null);
    setSectionSnapshots({});
    setSectionList(buildInitialSections());
    if (onSkip) onSkip();
    else if (onClose) onClose();
  }, [onSkip, onClose, buildInitialSections]);

  // Persist the snapshot the recorder emitted so a follow-up entry into
  // this section restores the exact slot layout + already-recorded clips.
  // Empty snapshots (no liveQuestions AND no slots) are treated as "nothing
  // to remember" — we drop the section's entry so the next entry mounts
  // fresh.
  const persistSectionSnapshot = useCallback((sectionId, snapshot) => {
    if (!sectionId) return;
    const hasContent = !!snapshot
      && ((Array.isArray(snapshot.liveQuestions) && snapshot.liveQuestions.length > 0)
        || (Array.isArray(snapshot.slots) && snapshot.slots.length > 0));
    setSectionSnapshots((prev) => {
      if (!hasContent) {
        if (!(sectionId in prev)) return prev;
        const next = { ...prev };
        delete next[sectionId];
        return next;
      }
      return { ...prev, [sectionId]: snapshot };
    });
  }, []);

  const handleRecordingCancel = useCallback((snapshot) => {
    // Back / cancel preserves the in-progress state so the user can pick up
    // where they left off (deleted questions stay deleted, recorded slots
    // stay recorded). The recorder passes its current snapshot as the only
    // arg — older call sites that fire onCancel() with no args still work
    // because snapshot is then undefined and persistSectionSnapshot is a
    // no-op for falsy input.
    persistSectionSnapshot(activeSectionTitle, snapshot);
    setActiveSectionTitle(null);
    setStep('hub');
  }, [activeSectionTitle, persistSectionSnapshot]);

  // Every clip captured in a section recorder belongs to that section
  // (preset prompts AND any custom "+ Add Question" ones). We use the
  // emitted snapshot as the source of truth — it carries both the user's
  // current liveQuestions (after their deletions/additions) and the
  // per-slot file/status info that lets re-entry restore the exact view.
  const handleSectionRecordingComplete = useCallback((files, meta) => {
    const snapshot = meta && meta.snapshot;
    if (snapshot) {
      persistSectionSnapshot(activeSectionTitle, snapshot);
    } else if (activeSectionTitle) {
      // Legacy fallback for older code paths that emit files only — wrap
      // them into a minimal snapshot so the section card still shows the
      // recordings.
      const filesArr = Array.isArray(files) ? files.filter(Boolean) : (files ? [files] : []);
      const fallback = {
        liveQuestions: [],
        slots: filesArr.map((file) => ({ status: 'done', file })),
      };
      persistSectionSnapshot(activeSectionTitle, fallback);
    }
    setActiveSectionTitle(null);
    setStep('hub');
  }, [activeSectionTitle, persistSectionSnapshot]);

  // Submit → flatten + emit + reset. Each clip carries its section's (edited)
  // title as the upload category.
  const handleSubmit = useCallback(() => {
    if (totalClips === 0) return;
    const allFiles = [];
    const fileSections = [];
    sectionList.forEach((s) => {
      const arr = clipsBySection[s.id] || [];
      arr.forEach((file) => {
        allFiles.push(file);
        fileSections.push(s.title);
      });
    });
    if (onComplete) onComplete(allFiles, { fileSections });
    setStep('hub');
    setActiveSectionTitle(null);
    setSectionSnapshots({});
    setSectionList(buildInitialSections());
  }, [clipsBySection, totalClips, onComplete, sectionList, buildInitialSections]);

  // Finish from hub. Normal-user flow (skipReview) submits immediately to start
  // the upload; otherwise we route through the review screen first.
  const handleFinish = useCallback(() => {
    if (totalClips === 0) return;
    if (skipReview) {
      handleSubmit();
      return;
    }
    setStep('review');
  }, [totalClips, skipReview, handleSubmit]);

  // Re-record from review → reopen that section's recorder. Overwrites the
  // section's stored clips when the user finishes (existing behavior).
  const handleReRecordFromReview = useCallback((sectionKey) => {
    setActiveSectionTitle(sectionKey);
    setStep('recording');
  }, []);

  const handleBackToHub = useCallback(() => {
    setStep('hub');
  }, []);

  // ── Make the browser Back button mirror the in-overlay Back (opt-in) ──
  // The overlay's "sections" (hub → recording/review, plus the skip / AI
  // modals) are React state on /gift-content, not routes, so the browser has no
  // history for them and a raw Back pops the whole route to the occasion page.
  // Standard SPA fix: push one history entry per FORWARD section change, and
  // unwind exactly one section on Back (popstate). On-screen Back buttons change
  // state directly, so when they move us shallower we pop the now-stale entry
  // (guarded so it doesn't re-trigger an unwind). We spread the existing React
  // Router history state into each entry so RR's {usr, key, idx} isn't clobbered.
  //
  // "Depth": 0 = closed (content view), 1 = hub, 2 = recording/review,
  // 3 = a skip / AI modal open on top of the hub.
  const modalOpen = skipConfirmOpen || aiModalOpen;
  let currentDepth = 0;
  if (open) {
    currentDepth = 1;
    if (step === 'recording' || step === 'review') currentDepth = 2;
    if (modalOpen) currentDepth = 3;
  }
  const depthRef = useRef(0);
  const fromPopRef = useRef(false);
  const ignorePopRef = useRef(false);
  useEffect(() => {
    if (!pinBrowserBack) return;
    const prev = depthRef.current;
    depthRef.current = currentDepth;
    if (currentDepth > prev) {
      // Went deeper → add a history entry the browser Back can pop.
      window.history.pushState({ ...(window.history.state || {}), pvmfLevel: currentDepth }, '');
    } else if (currentDepth < prev) {
      if (fromPopRef.current) {
        // Decrease driven by the browser Back we just handled — the entry is
        // already popped, nothing to do here.
        fromPopRef.current = false;
      } else {
        // An on-screen Back moved us shallower → pop the stale entry so the
        // browser history stays in sync. Ignore the popstate it triggers.
        ignorePopRef.current = true;
        window.history.back();
      }
    }
  }, [currentDepth, pinBrowserBack]);

  // One-step unwind, reassigned every render so the (once-registered) popstate
  // listener always runs against the current step / modal state.
  const overlayBackRef = useRef(() => {});
  overlayBackRef.current = () => {
    if (skipConfirmOpen) { setSkipConfirmOpen(false); return; }
    if (aiModalOpen) { closeAIModal(); return; }
    if (step === 'recording') { handleRecordingCancel(); return; } // → hub
    if (step === 'review') { handleBackToHub(); return; } // → hub
    handleHubBack(); // hub → close overlay back to the content view
  };
  useEffect(() => {
    if (!pinBrowserBack) return undefined;
    const onPopState = () => {
      if (ignorePopRef.current) { ignorePopRef.current = false; return; }
      if (!open) return; // already at the content view → let the natural Back run
      fromPopRef.current = true;
      overlayBackRef.current();
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [open, pinBrowserBack]);

  if (!open) return null;

  // ── Inline style atoms ──
  const overlayBase = {
    position: 'fixed',
    inset: 0,
    zIndex: 9550,
    // The login page's backdrop — the auth theme's .rays wash over #060608 —
    // so the hub/review screens share the surface of their host
    // (/gift-content) and the rest of the redesigned signup flow.
    background: LP.pageBg,
    color: LP.text,
    fontFamily: LP.font,
    overflowY: 'auto',
  };
  const closeBtnStyle = {
    position: 'absolute',
    top: '20px',
    right: '20px',
    width: '38px',
    height: '38px',
    borderRadius: '999px',
    border: `1px solid ${LP.border}`,
    background: LP.cardBg,
    backdropFilter: LP.blur,
    WebkitBackdropFilter: LP.blur,
    color: '#b7c0d1',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    zIndex: 1,
  };
  // Mirrors /gift-content's BACK control, which is now the auth flow's glass
  // pill. Positioning is owned by the top-bar flex row below so BACK and Skip
  // share one baseline.
  const backLinkStyle = {
    pointerEvents: 'auto',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '9px',
    padding: isMobile ? '7px 13px' : '9px 16px',
    border: `1px solid ${LP.border}`,
    borderRadius: '999px',
    background: LP.cardBg,
    backdropFilter: LP.blur,
    WebkitBackdropFilter: LP.blur,
    color: '#b7c0d1',
    cursor: 'pointer',
    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.05), 0 10px 24px -14px rgba(0, 0, 0, 0.85)',
    fontFamily: LP.font,
    fontSize: isMobile ? '10.5px' : '11.5px',
    fontWeight: 600,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    transition: 'color 200ms ease, border-color 200ms ease, background 200ms ease',
  };
  // The theme's filled primary — the same gradient pill as the login card's
  // submit and gift-content's Next.
  const primaryBtnStyle = (disabled) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '0 36px',
    minHeight: '48px',
    lineHeight: 1,
    borderRadius: '999px',
    border: 'none',
    background: disabled ? 'rgba(255, 255, 255, 0.07)' : LP.primaryBg,
    color: disabled ? 'rgba(255, 255, 255, 0.42)' : '#fff',
    fontFamily: LP.font,
    fontSize: '13px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    cursor: disabled ? 'not-allowed' : 'pointer',
    boxShadow: disabled ? 'none' : LP.primaryShadow,
  });
  // Its quieter companion — outlined glass, never grey.
  const secondaryBtnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 22px',
    minHeight: '44px',
    lineHeight: 1,
    borderRadius: '999px',
    border: `1px solid ${LP.border}`,
    background: LP.surface,
    color: LP.text,
    fontFamily: LP.font,
    fontSize: '12.5px',
    fontWeight: 600,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    cursor: 'pointer',
  };

  const renderTile = (tile) => {
    // Theme states: accent for a section that's under way (the palette has no
    // amber — that was the old theme's), green for finished, hairline for
    // untouched.
    const stateColors = {
      done: { badgeBg: 'rgba(90, 215, 122, 0.14)', badgeFg: '#5ad77a', border: 'rgba(90, 215, 122, 0.42)' },
      partial: { badgeBg: 'rgba(90, 165, 239, 0.16)', badgeFg: '#cfe4fb', border: 'rgba(90, 165, 239, 0.45)' },
      notStarted: { badgeBg: 'rgba(255, 255, 255, 0.06)', badgeFg: LP.muted, border: LP.border },
    };
    const c = stateColors[tile.state];
    // Same glass tile as gift-content's upload-source boxes.
    const tileStyle = {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      borderRadius: '18px',
      border: `1px solid ${c.border}`,
      background: LP.tileBg,
      backdropFilter: LP.tileBlur,
      WebkitBackdropFilter: LP.tileBlur,
      boxShadow: LP.tileShadow,
      color: LP.text,
      textAlign: 'left',
      cursor: 'pointer',
      transition: 'transform 260ms cubic-bezier(0.22, 1, 0.36, 1), border-color 240ms ease, box-shadow 260ms ease',
      fontFamily: LP.font,
      // Desktop tile shrunk to 120px so the hub (header + 3 rows of tiles +
      // Skip/Finish) fits inside one viewport without scroll. On phones the
      // title wraps so we keep auto height + a minHeight.
      height: isMobile ? 'auto' : '120px',
      minHeight: isMobile ? '100px' : undefined,
      width: '100%',
      boxSizing: 'border-box',
      overflow: isMobile ? 'visible' : 'hidden',
      padding: isMobile ? '12px 14px' : '14px 18px',
    };
    let badgeText;
    if (tile.state === 'done') badgeText = '✓ Done';
    else if (tile.state === 'partial' && tile.total != null) badgeText = `${tile.clipCount} of ${tile.total}`;
    else if (tile.state === 'partial') badgeText = `${tile.clipCount} clip${tile.clipCount === 1 ? '' : 's'}`;
    else badgeText = tile.total != null ? `${tile.total} prompts` : 'Add yours';

    const isEditing = editingSectionKey === tile.key;
    const smallEditBtn = {
      padding: '7px 16px',
      borderRadius: '999px',
      fontFamily: LP.font,
      fontSize: '11px',
      fontWeight: 600,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      cursor: 'pointer',
      lineHeight: 1,
    };

    if (isEditing) {
      // Edit mode — rename the section (becomes the upload category).
      // minHeight (not fixed height) + overflow visible so the compact
      // input/helper/buttons never get clipped by the tile's 150px box.
      return (
        <div
          key={tile.key}
          style={{
            ...tileStyle,
            height: 'auto',
            minHeight: '120px',
            overflow: 'visible',
            cursor: 'default',
            gap: '8px',
          }}
        >
          <input
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); saveEditSection(); }
              if (e.key === 'Escape') { e.preventDefault(); cancelEditSection(); }
            }}
            aria-label="Section name"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '0 14px',
              height: '44px',
              borderRadius: '14px',
              border: `1px solid ${LP.border}`,
              background: 'rgba(255, 255, 255, 0.04)',
              color: LP.text,
              fontFamily: LP.font,
              fontSize: '14.5px',
              fontWeight: 600,
              outline: 'none',
            }}
          />
          <p style={{ margin: 0, color: LP.muted, fontSize: '11.5px', lineHeight: 1.45 }}>
            This name becomes the category your clips are saved under.
          </p>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: 'auto' }}>
            <button
              type="button"
              onClick={cancelEditSection}
              style={{
                ...smallEditBtn,
                border: `1px solid ${LP.border}`,
                background: LP.surface,
                color: LP.text,
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveEditSection}
              style={{
                ...smallEditBtn,
                border: 'none',
                background: LP.primaryBg,
                color: '#fff',
                fontWeight: 700,
                boxShadow: LP.primaryShadow,
              }}
            >
              Save
            </button>
          </div>
        </div>
      );
    }

    // View mode — a div (not <button>) acting as a button so the inline
    // rename pencil can nest next to the title without invalid button-in-
    // button markup.
    return (
      <div
        key={tile.key}
        role="button"
        tabIndex={0}
        onClick={() => handleTileClick(tile.key)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleTileClick(tile.key);
          }
        }}
        // Hover lift has to be scripted: inline styles can't hold a :hover.
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.borderColor = LP.tileHoverBorder;
          e.currentTarget.style.boxShadow = LP.tileHoverShadow;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'none';
          e.currentTarget.style.borderColor = c.border;
          e.currentTarget.style.boxShadow = LP.tileShadow;
        }}
        style={tileStyle}
      >
        <CardRim inset={18} />
        {/* Header row — just title + status badge now. Edit / delete
            moved to a footer row at the bottom-right so the title can
            breathe horizontally. */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? '8px' : '10px',
        }}
        >
          <span style={{
            color: LP.text,
            fontSize: isMobile ? '15px' : '17px',
            fontWeight: 700,
            letterSpacing: '-0.01em',
            lineHeight: isMobile ? 1.3 : 1.25,
            // With no icons stealing horizontal room, the title can use the
            // full row width minus the badge. Allow up to 2 lines on desktop
            // before truncating with ellipsis — keeps long section names
            // readable.
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            wordBreak: isMobile ? 'break-word' : undefined,
            flex: 1,
            minWidth: 0,
          }}
          >
            {tile.title}
          </span>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '5px 11px',
            borderRadius: '999px',
            fontSize: '10.5px',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            background: c.badgeBg,
            color: c.badgeFg,
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
          >
            {badgeText}
          </span>
        </div>
        <p style={{
          margin: 0,
          color: LP.muted,
          fontSize: '13.5px',
          fontWeight: 500,
          lineHeight: 1.55,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
        >
          {tile.subtitle}
        </p>
        {/* Footer row — rename / delete icons, pinned to the bottom-right
            of the card via marginTop:auto. stopPropagation so they don't
            trigger the tile's record-section action. */}
        <div style={{
          marginTop: 'auto',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '6px',
        }}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); startEditSection(tile.key); }}
            aria-label={`Rename ${tile.title}`}
            title="Rename section"
            style={{
              flexShrink: 0,
              width: '28px',
              height: '28px',
              borderRadius: '999px',
              border: `1px solid ${LP.border}`,
              background: 'rgba(255, 255, 255, 0.06)',
              color: '#b7c0d1',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              transition: 'color 200ms ease, border-color 200ms ease, background 200ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = LP.borderStrong;
              e.currentTarget.style.background = LP.surfaceHover;
              e.currentTarget.style.color = '#fff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = LP.border;
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)';
              e.currentTarget.style.color = '#b7c0d1';
            }}
          >
            <PencilIcon />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleDeleteSection(tile.key); }}
            aria-label={`Delete ${tile.title}`}
            title="Delete section"
            style={{
              flexShrink: 0,
              width: '28px',
              height: '28px',
              borderRadius: '999px',
              border: '1px solid rgba(255, 107, 107, 0.3)',
              background: 'rgba(255, 107, 107, 0.1)',
              color: LP.danger,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              transition: 'border-color 200ms ease, background 200ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'rgba(255, 107, 107, 0.55)';
              e.currentTarget.style.background = 'rgba(255, 107, 107, 0.18)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(255, 107, 107, 0.3)';
              e.currentTarget.style.background = 'rgba(255, 107, 107, 0.1)';
            }}
          >
            <TrashIcon />
          </button>
        </div>
      </div>
    );
  };

  // ── review grid, mirroring the gift-flow sender's REVIEW phase ──────────
  // Same anatomy as GiftRecorder's .reviewGrid / .reviewCard / .reviewCardHeader
  // / .reviewCardIndex / .reviewCardSubject / .reviewCardMedia, so a receiver
  // reviewing their clips sees the screen the sender does. Values are copied
  // from GiftRecorder.module.css's REVIEW section.
  const reviewSectionTitleStyle = {
    margin: 0,
    color: LP.accent,
    fontSize: '13.5px',
    fontWeight: 700,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
  };
  const reviewGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gridAutoRows: 'minmax(180px, 240px)',
    gap: '16px',
    width: '100%',
    alignContent: 'start',
  };
  const reviewCardStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    height: '100%',
    minHeight: 0,
    padding: '12px',
    borderRadius: '18px',
    border: `1px solid ${LP.border}`,
    background: LP.cardBg,
    backdropFilter: LP.blur,
    WebkitBackdropFilter: LP.blur,
    boxShadow: LP.cardShadow,
  };
  const reviewCardHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    padding: '2px 2px 0',
    flex: '0 0 auto',
  };
  // Rounded square, accent-filled — the same chip as the sender's clip index.
  const reviewCardIndexStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: '24px',
    height: '24px',
    border: '1px solid rgba(90, 165, 239, 0.65)',
    borderRadius: '8px',
    background: 'linear-gradient(180deg, rgba(90, 165, 239, 0.42) 0%, rgba(90, 165, 239, 0.18) 100%)',
    color: '#fff',
    fontSize: '11.5px',
    fontWeight: 700,
    lineHeight: 1,
  };
  const reviewCardSubjectStyle = {
    flex: '1 1 auto',
    minWidth: 0,
    color: LP.text,
    fontSize: '13px',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    lineHeight: 1.35,
    textAlign: 'left',
  };
  // Footer buttons, sized exactly like the gift-flow review's .primaryButton /
  // .secondaryButton: 12px/28px and 12px/24px padding around a 16px label
  // (≈40px tall), pill radius, and the review footer's 240px min-width on the
  // primary. The primary keeps the theme's uppercase 700; the outlined
  // companion stays sentence case at 500, as it is there.
  const reviewFooterPrimaryStyle = (disabled) => ({
    ...primaryBtnStyle(disabled),
    padding: '12px 28px',
    minHeight: 0,
    minWidth: '240px',
    fontSize: '16px',
  });
  const reviewFooterSecondaryStyle = {
    ...secondaryBtnStyle,
    padding: '12px 24px',
    minHeight: 0,
    minWidth: '180px',
    fontSize: '16px',
    fontWeight: 500,
    letterSpacing: 'normal',
    textTransform: 'none',
  };

  const reviewCardMediaStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'hidden',
    border: `1px solid ${LP.border}`,
    borderRadius: '12px',
    background: '#06070a',
  };

  const renderReviewGroup = (label, sectionKey, clips) => {
    if (!clips.length) return null;
    return (
      <div key={sectionKey}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          margin: '24px 0 12px 0',
          gap: '12px',
        }}
        >
          <h2 style={reviewSectionTitleStyle}>
            {`${label} · ${clips.length}`}
          </h2>
          {/* Re-record is section-scoped in this flow, so it stays on the
              group header rather than on each card. */}
          <button
            type="button"
            onClick={() => handleReRecordFromReview(sectionKey)}
            style={{
              ...secondaryBtnStyle,
              padding: '0 18px',
              minHeight: '36px',
              fontSize: '11.5px',
            }}
          >
            Re-record
          </button>
        </div>
        <div style={reviewGridStyle}>
          {clips.map((file, i) => (
            <div key={`${sectionKey}-${i}`} style={reviewCardStyle}>
              <div style={reviewCardHeaderStyle}>
                <span style={reviewCardIndexStyle}>{i + 1}</span>
                <span style={reviewCardSubjectStyle}>{`Clip ${i + 1}`}</span>
              </div>
              <div style={reviewCardMediaStyle}>
                <NetflixPlayer
                  src={getPreviewUrl(file)}
                  poster={file.thumbnail || undefined}
                  fillHeight
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      {step === 'hub' && (
        <div
          aria-label={infoTitle}
          style={{
            ...overlayBase,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: isMobile ? '24px 16px' : '28px 24px',
          }}
        >
          <RaysLayer />
          {/* Top bar — BACK (left) + Skip (right) on one flex row. alignItems
              center makes their text share a single baseline, so the two are
              exactly vertically aligned regardless of the pill's box height.
              pointerEvents:none lets clicks pass through the row's empty span
              to the content below; the buttons re-enable their own clicks. */}
          <div style={{
            position: 'absolute',
            top: isMobile ? '14px' : '52px',
            left: 0,
            right: 0,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: isMobile ? '0 16px' : '0 60px',
            pointerEvents: 'none',
          }}
          >
            <button
              type="button"
              onClick={handleHubBack}
              aria-label="Back"
              style={backLinkStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#fff';
                e.currentTarget.style.borderColor = LP.borderStrong;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#b7c0d1';
                e.currentTarget.style.borderColor = LP.border;
              }}
            >
              <span aria-hidden="true" style={{ fontSize: '14px', lineHeight: 1 }}>←</span>
              BACK
            </button>
            {/* Same Skip control as the bottom CTA, duplicated up here where
                the old ✕ close used to sit. */}
            <button
              type="button"
              onClick={() => setSkipConfirmOpen(true)}
              style={{
                ...secondaryBtnStyle,
                pointerEvents: 'auto',
                // Web only: drop the Skip pill a touch lower than the BACK link.
                transform: isMobile ? 'none' : 'translateY(8px)',
                padding: isMobile ? '0 18px' : '0 24px',
                minHeight: isMobile ? '36px' : '40px',
                fontSize: isMobile ? '11px' : '11.5px',
                letterSpacing: '0.14em',
              }}
            >
              Skip
            </button>
          </div>
          <div style={{
            position: 'relative',
            zIndex: 1,
            width: '100%',
            maxWidth: '760px',
            marginTop: isMobile ? '48px' : 0,
          }}
          >
            <div style={{ textAlign: 'center' }}>
              {/* Reel icon + title on one line so the hero block takes less
                  vertical space and Skip/Finish stay visible without scroll. */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                marginBottom: '4px',
              }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: isMobile ? '40px' : '46px',
                    height: isMobile ? '40px' : '46px',
                    borderRadius: '14px',
                    border: '1px solid rgba(90, 165, 239, 0.28)',
                    background: 'linear-gradient(160deg, rgba(90, 165, 239, 0.18) 0%, rgba(90, 165, 239, 0.05) 100%)',
                    fontSize: isMobile ? '1.3rem' : '1.5rem',
                    lineHeight: 1,
                  }}
                >
                  🎬
                </span>
                <h1 style={{
                  margin: 0,
                  color: LP.text,
                  fontSize: isMobile ? '22px' : '32px',
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.15,
                }}
                >
                  {infoTitle}
                </h1>
              </div>
              {infoSubtitle && (
                <p style={{
                  margin: '10px auto 0 auto',
                  maxWidth: '520px',
                  color: LP.accent,
                  fontSize: isMobile ? '13px' : '14px',
                  fontWeight: 700,
                  letterSpacing: '-0.01em',
                  lineHeight: 1.45,
                }}
                >
                  {infoSubtitle}
                </p>
              )}
              <p style={{
                margin: '6px auto 26px auto',
                maxWidth: '520px',
                color: LP.muted,
                fontSize: isMobile ? '12.5px' : '13.5px',
                fontWeight: 500,
                lineHeight: 1.55,
              }}
              >
                {infoBody}
              </p>
            </div>

            <div style={{
              display: 'grid',
              // One column on phones so each card has the full width to show
              // the section title (question) in full + its intro line; 2-up
              // on tablets/desktop.
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))',
              gap: isMobile ? '8px' : '12px',
              marginBottom: '16px',
            }}
            >
              {tiles.map(renderTile)}
              {/* Add a new (custom) section. Dashed tile so it reads as an
                  affordance, not a recordable section. */}
              <button
                type="button"
                onClick={handleAddSection}
                aria-label="Add a section"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  height: isMobile ? '100px' : '120px',
                  width: '100%',
                  boxSizing: 'border-box',
                  borderRadius: '18px',
                  // A white hairline on a near-black page was almost invisible.
                  // This is the flow's established "add" affordance — the same
                  // dashed accent treatment as the recorder's "+ Add Question"
                  // tile (.addCustomBtn) — so it reads at rest, and hover just
                  // deepens it.
                  border: '1px dashed rgba(90, 165, 239, 0.5)',
                  background: 'rgba(90, 165, 239, 0.08)',
                  color: LP.accent,
                  cursor: 'pointer',
                  fontFamily: LP.font,
                  transition: 'transform 260ms cubic-bezier(0.22, 1, 0.36, 1), background 220ms ease, border-color 220ms ease, color 220ms ease, box-shadow 260ms ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.borderColor = 'rgba(90, 165, 239, 0.85)';
                  e.currentTarget.style.background = 'rgba(90, 165, 239, 0.18)';
                  e.currentTarget.style.color = '#fff';
                  e.currentTarget.style.boxShadow = '0 18px 40px -22px rgba(90, 165, 239, 0.55)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.borderColor = 'rgba(90, 165, 239, 0.5)';
                  e.currentTarget.style.background = 'rgba(90, 165, 239, 0.08)';
                  e.currentTarget.style.color = LP.accent;
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <PlusIcon size={20} />
                <span style={{
                  fontSize: '12.5px',
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                }}
                >
                  Add section
                </span>
              </button>
            </div>

            {/* Two equal columns rather than a flex row: matching padding still
                left the pair different widths (the labels differ in length), so
                the grid fixes the cell width and both buttons fill it. */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile
                ? 'repeat(2, minmax(0, 1fr))'
                : 'repeat(2, minmax(0, 220px))',
              gap: '12px',
              justifyContent: 'center',
            }}
            >
              {/* Skip lets the user bail on the whole flow without recording
                  anything. Sized to match the Finish CTA so they read as a
                  matched primary/secondary pair. */}
              <button
                type="button"
                onClick={() => setSkipConfirmOpen(true)}
                style={{
                  ...secondaryBtnStyle, width: '100%', padding: '0 20px', minHeight: '52px', fontSize: '13px',
                }}
              >
                Skip
              </button>
              <button
                type="button"
                onClick={handleFinish}
                disabled={totalClips === 0}
                style={{
                  ...primaryBtnStyle(totalClips === 0), width: '100%', padding: '0 20px', minHeight: '52px',
                }}
              >
                {totalClips === 0 ? 'Finish' : `Finish · ${totalClips} clip${totalClips === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>

          {/* Skip confirmation — same surface as the "Add a section" modal
              (the theme's glass card on a dark scrim). Warns the user that
              skipping retires this page; Back (which clears the flag) is the
              way to keep it. */}
          {skipConfirmOpen && (
            (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
              <div
                role="dialog"
              aria-modal="true"
              aria-label="Skip recording your memories?"
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 9700,
                background: LP.scrim,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
              }}
              onClick={(e) => { if (e.target === e.currentTarget) setSkipConfirmOpen(false); }}
              >
              <div style={{
                position: 'relative',
                width: '100%',
                maxWidth: '440px',
                borderRadius: '24px',
                background: LP.modalBg,
                backdropFilter: LP.blur,
                WebkitBackdropFilter: LP.blur,
                border: `1px solid ${LP.border}`,
                boxShadow: LP.cardShadow,
                color: LP.text,
                fontFamily: LP.font,
                padding: isMobile ? '26px 20px' : '30px 26px',
                textAlign: 'center',
              }}
              >
                <CardRim />
                <div style={{ fontSize: '2rem', lineHeight: 1, marginBottom: '10px' }} aria-hidden="true">🎬</div>
                <h2 style={{
                  margin: 0,
                  color: LP.text,
                  fontSize: isMobile ? '19px' : '22px',
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.2,
                }}
                >
                  Don&apos;t you want to record your memories?
                </h2>
                <p style={{
                  margin: '10px auto 0 auto',
                  maxWidth: '360px',
                  color: LP.muted,
                  fontSize: isMobile ? '12.5px' : '13.5px',
                  fontWeight: 500,
                  lineHeight: 1.55,
                }}
                >
                  Your stories become a lasting part of your Projector.
                  You can always come back and add more.
                </p>
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  marginTop: '22px',
                }}
                >
                  <button
                    type="button"
                    onClick={() => setSkipConfirmOpen(false)}
                    style={{ ...primaryBtnStyle(false), padding: '0 26px', minHeight: '46px' }}
                  >
                    Keep recording
                  </button>
                  <button
                    type="button"
                    onClick={() => { setSkipConfirmOpen(false); handleHubSkip(); }}
                    style={{ ...secondaryBtnStyle, padding: '0 26px', minHeight: '46px' }}
                  >
                    Skip anyway
                  </button>
                </div>
              </div>
              </div>
)
          )}
        </div>
      )}
      {step === 'review' && (
        <div
          aria-label="Review your recordings"
          style={{
            ...overlayBase,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '56px 24px',
          }}
        >
          <RaysLayer />
          <button type="button" onClick={handleHubClose} aria-label="Close" style={closeBtnStyle}>✕</button>
          <div style={{
            position: 'relative', zIndex: 1, width: '100%', maxWidth: '880px',
          }}
          >
            <div style={{ marginBottom: '24px', textAlign: 'center' }}>
              <h1 style={{
                margin: '0 0 8px 0',
                color: LP.text,
                fontSize: isMobile ? '22px' : '32px',
                fontWeight: 700,
                letterSpacing: '-0.02em',
                lineHeight: 1.15,
              }}
              >
                Review your recordings
              </h1>
              <p style={{
                margin: 0,
                color: LP.muted,
                fontSize: '13.5px',
                fontWeight: 500,
                lineHeight: 1.55,
              }}
              >
                {`${totalClips} clip${totalClips === 1 ? '' : 's'} ready. Re-record any section, or submit when you're happy.`}
              </p>
            </div>

            <div>
              {sectionList.map((s) => (
                renderReviewGroup(s.title, s.id, clipsBySection[s.id] || [])
              ))}
            </div>

            {/* Footer bar — the gift-flow review's .reviewFooter: a hairline to
                sit on, and a primary wide enough to anchor the page. */}
            <div style={{
              display: 'flex',
              gap: '14px',
              justifyContent: 'center',
              flexWrap: 'wrap',
              marginTop: '26px',
              paddingTop: '16px',
              borderTop: `1px solid ${LP.border}`,
            }}
            >
              {/* On mobile the two buttons share the row equally (flex:1) with
                  matching height/padding/font so they read as the same size;
                  only their colour (outline vs gradient) differs. */}
              <button
                type="button"
                onClick={handleBackToHub}
                style={isMobile
                  ? { ...reviewFooterSecondaryStyle, flex: 1, minWidth: 0, padding: '12px 18px' }
                  : reviewFooterSecondaryStyle}
              >
                Back to tiles
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={totalClips === 0}
                style={isMobile
                  ? { ...reviewFooterPrimaryStyle(totalClips === 0), flex: 1, minWidth: 0, padding: '12px 18px' }
                  : reviewFooterPrimaryStyle(totalClips === 0)}
              >
                {`Submit · ${totalClips} clip${totalClips === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </div>
      )}
      {step === 'recording' && activeSectionTitle && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9500,
            // GiftRecorder below is the sender flow's recorder, unchanged — so
            // this only supplies the surface it sits on there. In gift-flow that
            // page is a themed step, which means the same light-ray backdrop
            // (RaysLayer below) over the near-black base.
            background: '#060608',
            overflow: 'auto',
          }}
        >
          <RaysLayer />
          {/* Page-level Back — fixed to the viewport's top-left corner so it
              clearly reads as "leave this screen / return to the tiles"
              rather than an in-recorder control. Same top/left offset as
              GiftRecorder's own .cornerFinish (top-right) so the two sit on
              one shared baseline instead of Back floating higher. */}
          <button
            type="button"
            onClick={handleRecordingCancel}
            aria-label="Back to sections"
            style={{
              ...backLinkStyle,
              position: 'fixed',
              top: isMobile ? '18px' : '36px',
              left: isMobile ? '18px' : '36px',
              zIndex: 9600,
            }}
          >
            <span aria-hidden="true" style={{ fontSize: '14px', lineHeight: 1 }}>←</span>
            <span>Back</span>
          </button>
          <div
            style={{
              position: 'relative',
              zIndex: 1,
              minHeight: '100vh',
              display: 'flex',
              // On phones the recorder header ("Where it all started…") would
              // otherwise center under the fixed top-left Back button and
              // overlap it, so top-align and add clearance below the button.
              alignItems: isMobile ? 'flex-start' : 'center',
              justifyContent: 'center',
              // Extra top clearance beyond the Back/Finish pills (now at 36px)
              // so the section heading doesn't start flush against them.
              padding: '72px 0 24px',
              boxSizing: 'border-box',
            }}
          >
            {(() => {
              const activeSection = getSectionById(activeSectionTitle);
              if (!activeSection) {
                handleRecordingCancel();
                return null;
              }
              // Restore the user's last view of this section if we kept a
              // snapshot — preserves slot edits + recorded clips across
              // hub ↔ recorder navigation.
              const savedSnapshot = sectionSnapshots[activeSection.id];
              // Custom / added / "Other" section (no preset prompts) →
              // custom-only mode: open the add-question modal immediately
              // (autoOpenAddQuestion). skipReview is OMITTED so the user
              // gives an explicit Attach when done (no natural stop).
              if (!activeSection.questions || activeSection.questions.length === 0) {
                return (
                  <GiftRecorder
                    questions={[]}
                    recipientName=""
                    occasionLabel={activeSection.title}
                    autoOpenAddQuestion={!savedSnapshot}
                    initialState={savedSnapshot}
                    onComplete={handleSectionRecordingComplete}
                    onCancel={handleRecordingCancel}
                    showInRecorderFinish
                  />
                );
              }
              // Preset section → skipReview suppresses GiftRecorder's review
              // grid; the hub-level review handles the full preview later.
              // Pass the section (with its current/edited title) for the
              // recorder header.
              return (
                <GiftRecorder
                  questions={activeSection.questions}
                  recipientName=""
                  occasionLabel={activeSection.title}
                  initialState={savedSnapshot}
                  sections={[{
                    title: activeSection.title,
                    intro: activeSection.intro,
                    questions: activeSection.questions,
                  }]}
                  skipReview
                  onComplete={handleSectionRecordingComplete}
                  onCancel={handleRecordingCancel}
                  showInRecorderFinish
                />
              );
            })()}
          </div>
        </div>
      )}
      {/* AI section creation modal. Rendered on top of the hub at high
          z-index so the user can cancel back to the tile grid at any step.
          Steps: 'choose' → 'describe' → 'review'. */}
      {aiModalOpen && (
        (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
          <div
            role="dialog"
          aria-modal="true"
          aria-label="Add a section"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9700,
            background: LP.scrim,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) closeAIModal(); }}
          >
          <div style={{
            position: 'relative',
            width: '100%',
            maxWidth: '560px',
            maxHeight: 'calc(100vh - 40px)',
            overflow: 'auto',
            borderRadius: '24px',
            background: LP.modalBg,
            backdropFilter: LP.blur,
            WebkitBackdropFilter: LP.blur,
            border: `1px solid ${LP.border}`,
            boxShadow: LP.cardShadow,
            color: LP.text,
            fontFamily: LP.font,
          }}
          >
            <CardRim />
            {/* Local keyframes + hover styles for this modal only. Scoped
                via class names that begin with `ai…` to avoid clashing. */}
            <style>
{`
              @keyframes aiSparklePulse {
                0%, 100% { transform: scale(1); opacity: 0.9; }
                50% { transform: scale(1.18); opacity: 1; }
              }
              @keyframes aiHaloSpin {
                from { transform: rotate(0deg); }
                to   { transform: rotate(360deg); }
              }
              @keyframes aiHaloPulse {
                0%, 100% { opacity: 0.55; }
                50%      { opacity: 1; }
              }
              @keyframes aiDotBounce {
                0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
                40%           { transform: translateY(-6px); opacity: 1; }
              }
              @keyframes aiShimmer {
                0%   { background-position: -240px 0; }
                100% { background-position: 240px 0; }
              }
              @keyframes aiStarFloat {
                0%   { transform: translate(0, 0) scale(0.6); opacity: 0; }
                30%  { opacity: 1; }
                100% { transform: translate(var(--dx), var(--dy)) scale(1); opacity: 0; }
              }
              .aiOptionCard {
                transition: transform 180ms ease, background 180ms ease, border-color 180ms ease, box-shadow 180ms ease;
              }
              .aiOptionCard:hover {
                transform: translateY(-3px);
                background: rgba(255,255,255,0.085) !important;
                border-color: rgba(90,165,239,0.5) !important;
                box-shadow: 0 18px 40px -22px rgba(90,165,239,0.55);
              }
              .aiOptionCardAI:hover {
                background: linear-gradient(180deg, rgba(255,204,74,0.16), rgba(255,204,74,0.04)) !important;
                border-color: rgba(255,204,74,0.65) !important;
                box-shadow: 0 10px 24px rgba(255,204,74,0.18);
              }
              .aiGenSparkle {
                animation: aiSparklePulse 1.4s ease-in-out infinite;
              }
              .aiGenHalo {
                animation: aiHaloSpin 4s linear infinite, aiHaloPulse 2s ease-in-out infinite;
              }
              .aiGenDot {
                display: inline-block;
                width: 8px; height: 8px;
                margin: 0 4px;
                border-radius: 999px;
                background: #ffcc4a;
                animation: aiDotBounce 1.4s ease-in-out infinite both;
              }
              .aiGenDot:nth-child(1) { animation-delay: -0.32s; }
              .aiGenDot:nth-child(2) { animation-delay: -0.16s; }
              .aiGenShimmer {
                background: linear-gradient(
                  90deg,
                  rgba(255,255,255,0.55) 0%,
                  rgba(255,204,74,1) 50%,
                  rgba(255,255,255,0.55) 100%
                );
                background-size: 240px 100%;
                -webkit-background-clip: text;
                background-clip: text;
                -webkit-text-fill-color: transparent;
                animation: aiShimmer 1.8s linear infinite;
              }
              .aiGenStar {
                position: absolute;
                top: 50%; left: 50%;
                font-size: 0.8rem;
                color: #ffcc4a;
                animation: aiStarFloat 2s ease-out infinite;
                pointer-events: none;
              }
            `}
            </style>

            {/* Header — same across all steps */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '20px 24px',
              borderBottom: `1px solid ${LP.border}`,
            }}
            >
              <span style={{
                color: LP.text, fontSize: '17px', fontWeight: 700, letterSpacing: '-0.02em',
              }}
              >
                {aiLoading && 'Generating your questions'}
                {!aiLoading && aiStep === 'choose' && 'Add a section'}
                {!aiLoading && aiStep === 'describe' && 'Describe your section'}
                {!aiLoading && aiStep === 'review' && 'Review your section'}
              </span>
              <button
                type="button"
                onClick={closeAIModal}
                aria-label="Close"
                style={{
                  width: '34px',
                  height: '34px',
                  borderRadius: '999px',
                  border: `1px solid ${LP.border}`,
                  background: 'rgba(255, 255, 255, 0.06)',
                  color: '#b7c0d1',
                  cursor: 'pointer',
                  fontFamily: LP.font,
                }}
              >
                ✕
              </button>
            </div>

            {/* Step: choose */}
            {/* Generating view — shown for both initial Generate and
                Regenerate. Replaces the form so the animation gets the full
                modal stage. */}
            {aiLoading && (
              <div style={{
                padding: '40px 24px 44px 24px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '260px',
                position: 'relative',
                overflow: 'hidden',
              }}
              >
                {/* Sparkle + rotating halo */}
                <div style={{
                  position: 'relative',
                  width: '120px',
                  height: '120px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '22px',
                }}
                >
                  {/* Halo: conic-gradient ring that spins + pulses */}
                  <div
                    aria-hidden="true"
                    className="aiGenHalo"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '999px',
                      background: 'conic-gradient(from 0deg, rgba(255,204,74,0) 0deg, rgba(255,204,74,0.85) 90deg, rgba(255,204,74,0) 200deg, rgba(255,204,74,0.45) 320deg, rgba(255,204,74,0) 360deg)',
                      filter: 'blur(2px)',
                      maskImage: 'radial-gradient(circle, transparent 38%, black 42%, black 60%, transparent 64%)',
                      WebkitMaskImage: 'radial-gradient(circle, transparent 38%, black 42%, black 60%, transparent 64%)',
                    }}
                  />
                  {/* Soft glow under the sparkle */}
                  <div
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      inset: '18px',
                      borderRadius: '999px',
                      background: 'radial-gradient(circle, rgba(255,204,74,0.30), rgba(255,204,74,0) 70%)',
                    }}
                  />
                  {/* Floating stars — staggered offsets via --dx/--dy. */}
                  <span aria-hidden="true" className="aiGenStar" style={{ '--dx': '-42px', '--dy': '-32px', animationDelay: '0s' }}>✦</span>
                  <span aria-hidden="true" className="aiGenStar" style={{ '--dx': '36px', '--dy': '-40px', animationDelay: '0.5s' }}>✦</span>
                  <span aria-hidden="true" className="aiGenStar" style={{ '--dx': '40px', '--dy': '34px', animationDelay: '1s' }}>✦</span>
                  <span aria-hidden="true" className="aiGenStar" style={{ '--dx': '-38px', '--dy': '36px', animationDelay: '1.5s' }}>✦</span>
                  {/* The sparkle itself — pulses gently */}
                  <span
                    aria-hidden="true"
                    className="aiGenSparkle"
                    style={{
                      position: 'relative',
                      fontSize: '2.6rem',
                      filter: 'drop-shadow(0 4px 14px rgba(255,204,74,0.55))',
                    }}
                  >
                    ✨
                  </span>
                </div>

                <div
                  className="aiGenShimmer"
                  style={{
                    fontSize: '1.1rem',
                    fontWeight: 800,
                    letterSpacing: '0.01em',
                  }}
                >
                  Crafting your questions…
                </div>
                <div style={{
                  marginTop: '10px',
                  color: LP.muted,
                  fontSize: '12.5px',
                  fontWeight: 500,
                  textAlign: 'center',
                  maxWidth: '380px',
                  lineHeight: 1.55,
                }}
                >
                  {aiDescription
                    ? `Thinking about “${aiDescription.length > 60 ? `${aiDescription.slice(0, 57)}…` : aiDescription}”`
                    : 'Calling on the muse'}
                </div>
                <div aria-hidden="true" style={{ marginTop: '18px', height: '12px' }}>
                  <span className="aiGenDot" />
                  <span className="aiGenDot" />
                  <span className="aiGenDot" />
                </div>
              </div>
            )}

            {aiStep === 'choose' && !aiLoading && (
              <div style={{ padding: '24px 22px 26px 22px' }}>
                <p style={{
                  margin: '0 0 22px 0',
                  color: LP.muted,
                  fontSize: '13.5px',
                  fontWeight: 500,
                  lineHeight: 1.55,
                  textAlign: 'center',
                }}
                >
                  How would you like to create this section?
                </p>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: '12px',
                }}
                >
                  <button
                    type="button"
                    onClick={handleAIChooseAI}
                    className="aiOptionCard aiOptionCardAI"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '22px 16px',
                      minHeight: '180px',
                      borderRadius: '16px',
                      border: '1px solid rgba(255,204,74,0.35)',
                      background: 'linear-gradient(180deg, rgba(255,204,74,0.08), rgba(255,204,74,0.02))',
                      color: '#fff',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      textAlign: 'center',
                    }}
                  >
                    <span
                      aria-hidden="true"
                      className="aiOptionIcon aiOptionIconAI"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '56px',
                        height: '56px',
                        borderRadius: '999px',
                        background: 'radial-gradient(circle at center, rgba(255,204,74,0.30), rgba(255,204,74,0.08))',
                        border: '1px solid rgba(255,204,74,0.40)',
                        fontSize: '1.7rem',
                      }}
                    >
                      ✨
                    </span>
                    <div>
                      <div style={{
                        color: LP.text, fontWeight: 700, fontSize: '16px', letterSpacing: '-0.01em', marginBottom: '5px',
                      }}
                      >
                        Generate with AI
                      </div>
                      <div style={{ color: LP.muted, fontSize: '12.5px', fontWeight: 500, lineHeight: 1.5 }}>
                        Describe the topic and we&apos;ll suggest questions.
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={handleAIChooseManual}
                    className="aiOptionCard"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '22px 16px',
                      minHeight: '180px',
                      borderRadius: '18px',
                      border: `1px solid ${LP.border}`,
                      background: LP.surface,
                      color: LP.text,
                      cursor: 'pointer',
                      fontFamily: LP.font,
                      textAlign: 'center',
                    }}
                  >
                    <span
                      aria-hidden="true"
                      className="aiOptionIcon"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '56px',
                        height: '56px',
                        borderRadius: '999px',
                        background: 'linear-gradient(160deg, rgba(90, 165, 239, 0.18) 0%, rgba(90, 165, 239, 0.05) 100%)',
                        border: '1px solid rgba(90, 165, 239, 0.28)',
                        fontSize: '1.6rem',
                      }}
                    >
                      ✍️
                    </span>
                    <div>
                      <div style={{
                        color: LP.text, fontWeight: 700, fontSize: '16px', letterSpacing: '-0.01em', marginBottom: '5px',
                      }}
                      >
                        Create manually
                      </div>
                      <div style={{ color: LP.muted, fontSize: '12.5px', fontWeight: 500, lineHeight: 1.5 }}>
                        Add a blank section and write your own.
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Step: describe */}
            {aiStep === 'describe' && !aiLoading && (
              <div style={{ padding: '22px' }}>
                <label
                  htmlFor="ai-section-description"
                  style={{
                    display: 'block',
                    color: LP.text,
                    fontSize: '15px',
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    marginBottom: '10px',
                  }}
                >
                  What would you like this section to be about?
                </label>
                <textarea
                  id="ai-section-description"
                  value={aiDescription}
                  onChange={(e) => setAIDescription(e.target.value)}
                  placeholder='e.g. "My time in the army — the people I met, the lessons that stayed with me"'
                  rows={4}
                  disabled={aiLoading}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '14px 16px',
                    borderRadius: '14px',
                    border: `1px solid ${LP.border}`,
                    background: 'rgba(255, 255, 255, 0.04)',
                    color: LP.text,
                    fontFamily: LP.font,
                    fontSize: '15px',
                    fontWeight: 500,
                    lineHeight: 1.55,
                    resize: 'vertical',
                    outline: 'none',
                  }}
                />
                {aiError && (
                  <div style={{
                    marginTop: '10px',
padding: '10px 12px',
                    borderRadius: '14px',
                    background: 'rgba(255, 107, 107, 0.1)',
                    border: '1px solid rgba(255, 107, 107, 0.28)',
                    color: '#ffb5b5',
                    fontSize: '12.5px',
                    fontWeight: 500,
                  }}
                  >
                    {aiError}
                  </div>
                )}
                <div style={{
                  display: 'flex',
gap: '10px',
                  justifyContent: 'flex-end',
marginTop: '18px',
                }}
                >
                  {/* On mobile both buttons share the row equally (flex:1) with
                      matching padding/height/font so Cancel and Generate read
                      as the same size; only colour differs. */}
                  <button
                    type="button"
                    onClick={closeAIModal}
                    disabled={aiLoading}
                    style={{
                      ...secondaryBtnStyle,
                      padding: '0 20px',
                      minHeight: isMobile ? '46px' : '42px',
                      fontSize: '12px',
                      ...(isMobile ? { flex: 1 } : {}),
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleAIGenerate}
                    disabled={aiLoading || !aiDescription.trim()}
                    style={{
                      ...primaryBtnStyle(aiLoading || !aiDescription.trim()),
                      padding: '0 24px',
                      minHeight: isMobile ? '46px' : '42px',
                      fontSize: '12px',
                      ...(isMobile ? { flex: 1 } : {}),
                    }}
                  >
                    {aiLoading ? 'Generating…' : 'Generate'}
                  </button>
                </div>
              </div>
            )}

            {/* Step: review */}
            {aiStep === 'review' && aiResult && !aiLoading && (
              <div style={{ padding: '22px' }}>
                <label
                  htmlFor="ai-section-title"
                  style={{
                    display: 'block',
                    color: LP.text,
                    fontSize: '15px',
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    marginBottom: '10px',
                  }}
                >
                  Section title
                </label>
                <input
                  id="ai-section-title"
                  type="text"
                  value={aiResult.title}
                  onChange={(e) => handleAITitleChange(e.target.value)}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '0 16px',
                    height: '50px',
                    borderRadius: '14px',
                    border: `1px solid ${LP.border}`,
                    background: 'rgba(255, 255, 255, 0.04)',
                    color: LP.text,
                    fontFamily: LP.font,
                    fontSize: '15px',
                    fontWeight: 600,
                    outline: 'none',
                    marginBottom: '20px',
                  }}
                />
                <div style={{
                  display: 'flex',
alignItems: 'center',
justifyContent: 'space-between',
                  marginBottom: '8px',
                }}
                >
                  <span style={{
                    color: LP.text, fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
                  }}
                  >
                    Questions
                  </span>
                  <span style={{ color: LP.muted, fontSize: '11.5px', fontWeight: 500 }}>
                    {aiResult.questions.length}
{' '}
total
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {aiResult.questions.map((q, idx) => {
                    const isEditing = aiEditingIdx === idx;
                    return (
                      // eslint-disable-next-line react/no-array-index-key
                      <div
key={idx}
style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '10px',
                        padding: '12px 14px',
                        borderRadius: '14px',
                        background: LP.surface,
                        border: `1px solid ${LP.border}`,
                      }}
                      >
                        <span style={{
                          color: LP.accent,
                          fontSize: '12.5px',
                          fontWeight: 700,
                          minWidth: '20px',
                          paddingTop: '3px',
                        }}
                        >
                          {idx + 1}
.
                        </span>
                        {isEditing ? (
                          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <textarea
                              value={aiEditingValue}
                              onChange={(e) => setAIEditingValue(e.target.value)}
                              rows={2}
                              autoFocus
                              style={{
                                width: '100%',
                                boxSizing: 'border-box',
                                padding: '10px 12px',
                                borderRadius: '12px',
                                border: `1px solid ${LP.border}`,
                                background: 'rgba(255, 255, 255, 0.04)',
                                color: LP.text,
                                fontFamily: LP.font,
                                fontSize: '13.5px',
                                fontWeight: 500,
                                lineHeight: 1.5,
                                resize: 'vertical',
                                outline: 'none',
                              }}
                            />
                            <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                              <button
                                type="button"
                                onClick={handleAICancelEditQuestion}
                                style={{
                                  padding: '7px 14px',
                                  borderRadius: '999px',
                                  border: `1px solid ${LP.border}`,
                                  background: LP.surface,
                                  color: LP.text,
                                  cursor: 'pointer',
                                  fontFamily: LP.font,
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  letterSpacing: '0.06em',
                                  textTransform: 'uppercase',
                                }}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={handleAISaveEditQuestion}
                                style={{
                                  padding: '7px 14px',
                                  borderRadius: '999px',
                                  border: 'none',
                                  background: LP.primaryBg,
                                  color: '#fff',
                                  cursor: 'pointer',
                                  fontFamily: LP.font,
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  letterSpacing: '0.06em',
                                  textTransform: 'uppercase',
                                  boxShadow: LP.primaryShadow,
                                }}
                              >
                                Save
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <span style={{
                              flex: 1, color: LP.text, fontSize: '13.5px', fontWeight: 500, lineHeight: 1.5,
                            }}
                            >
                              {q}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleAIStartEditQuestion(idx)}
                              aria-label={`Edit question ${idx + 1}`}
                              title="Edit"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                background: 'transparent',
                                border: 'none',
                                color: '#b7c0d1',
                                cursor: 'pointer',
                                padding: '3px 6px',
                              }}
                            >
                              <PencilIcon size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleAIDeleteQuestion(idx)}
                              aria-label={`Delete question ${idx + 1}`}
                              title="Delete"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                background: 'transparent',
                                border: 'none',
                                color: LP.danger,
                                cursor: 'pointer',
                                padding: '3px 6px',
                              }}
                            >
                              <TrashIcon size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div style={{
                  display: 'flex',
gap: '10px',
flexWrap: 'wrap',
                  marginTop: '14px',
                }}
                >
                  <button
                    type="button"
                    onClick={handleAIAddQuestion}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '7px',
                      padding: '9px 16px',
                      borderRadius: '999px',
                      border: `1px dashed ${LP.borderStrong}`,
                      background: 'transparent',
                      color: LP.muted,
                      cursor: 'pointer',
                      fontFamily: LP.font,
                      fontSize: '11.5px',
                      fontWeight: 600,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                    }}
                  >
                    <PlusIcon size={14} />
                    Add question
                  </button>
                  {/* <button
                    type="button"
                    onClick={handleAIRegenerate}
                    disabled={aiLoading}
                    style={{
                      padding: '8px 14px',
borderRadius: '999px',
                      border: '1px solid rgba(255,255,255,0.18)',
                      background: 'rgba(255,255,255,0.06)',
                      color: '#cfd1da',
cursor: aiLoading ? 'default' : 'pointer',
                      fontFamily: 'inherit',
fontSize: '0.82rem',
fontWeight: 700,
                    }}
                  >
                    {aiLoading ? 'Regenerating…' : '⟲ Regenerate'}
                  </button> */}
                  <button
                    type="button"
                    onClick={handleAIBackToDescribe}
                    disabled={aiLoading}
                    style={{
                      ...secondaryBtnStyle,
                      padding: '0 16px',
                      minHeight: '38px',
                      fontSize: '11.5px',
                      cursor: aiLoading ? 'default' : 'pointer',
                    }}
                  >
                    Edit description
                  </button>
                </div>
                {aiError && (
                  <div style={{
                    marginTop: '12px',
padding: '10px 12px',
                    borderRadius: '14px',
                    background: 'rgba(255, 107, 107, 0.1)',
                    border: '1px solid rgba(255, 107, 107, 0.28)',
                    color: '#ffb5b5',
                    fontSize: '12.5px',
                    fontWeight: 500,
                  }}
                  >
                    {aiError}
                  </div>
                )}
                <div style={{
                  display: 'flex',
gap: '10px',
                  justifyContent: 'flex-end',
marginTop: '20px',
                  paddingTop: '18px',
                  borderTop: `1px solid ${LP.border}`,
                }}
                >
                  <button
                    type="button"
                    onClick={closeAIModal}
                    disabled={aiLoading}
                    style={{ ...secondaryBtnStyle, padding: '0 20px', minHeight: '42px', fontSize: '12px' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleAICommitSection}
                    disabled={aiLoading || aiResult.questions.length === 0}
                    style={{
                      ...primaryBtnStyle(aiLoading || aiResult.questions.length === 0),
                      padding: '0 24px',
                      minHeight: '42px',
                      fontSize: '12px',
                    }}
                  >
                    Add section
                  </button>
                </div>
              </div>
            )}
          </div>
          </div>
)
      )}
    </>
  );
};

/**
 * "Tell Your Story" flow (with the guest-invite section). Shared by the gift
 * lanes, signup and the in-app upload dropdown, so it gates itself rather than
 * relying on each host to pick a variant.
 */
export const PersonalVideoMessageFlow = (props) => (
  isNewUIEnabled() ? <PersonalVideoMessageFlowNew {...props} /> : <PersonalVideoMessageFlowClassic {...props} />
);

export default PersonalVideoMessageFlow;
